import { useEffect, useState } from "react";
import { Webhook, RefreshCw, KeyRound, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const EVENT_STATUS_CLS = {
  dry_run: "bg-slate-500/10 text-slate-500 border-slate-400/30",
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  sent: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  failed: "bg-red-500/10 text-red-600 border-red-500/30",
};

export default function AdminCRM() {
  const { t } = useLang();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [events, setEvents] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ api_key: "", location_id: "", webhook_secret: "", enabled: false });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const isSuperAdmin = user?.role === "super_admin";

  const load = () => {
    Promise.all([api.get("/crm/settings"), api.get("/crm/events"), api.get("/crm/webhooks")])
      .then(([s, e, w]) => {
        setSettings(s.data);
        setForm((f) => ({ ...f, location_id: s.data.location_id || "", enabled: s.data.enabled }));
        setEvents(e.data);
        setWebhooks(w.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const saveSettings = async () => {
    try {
      const payload = { location_id: form.location_id, enabled: form.enabled };
      if (form.api_key) payload.api_key = form.api_key;
      if (form.webhook_secret) payload.webhook_secret = form.webhook_secret;
      const res = await api.patch("/crm/settings", payload);
      setSettings(res.data);
      setForm((f) => ({ ...f, api_key: "", webhook_secret: "" }));
      toast.success(t("CRM-Einstellungen gespeichert", "CRM settings saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Nur Super Admins k\u00f6nnen CRM-Einstellungen \u00e4ndern", "Only super admins can change CRM settings"));
    }
  };

  const retry = async () => {
    try {
      const res = await api.post("/crm/events/retry");
      if (res.data.skipped) {
        toast.info(t("GoHighLevel ist nicht konfiguriert \u2013 Events bleiben im Dry-Run.", "GoHighLevel is not configured \u2013 events remain in dry-run."));
      } else {
        toast.success(`${res.data.synced} ${t("synchronisiert", "synced")}, ${res.data.failed} ${t("fehlgeschlagen", "failed")}`);
      }
      load();
    } catch (e) {
      toast.error(t("Fehler beim Wiederholen", "Error retrying"));
    }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-96 rounded-[16px]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-crm-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">CRM / GoHighLevel</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("Integration, Outbox-Events und Webhook-Logs", "Integration, outbox events and webhook logs")}</p>
        </div>
        <Badge variant="outline" className={`rounded-full px-3 py-1.5 gap-1.5 ${settings?.configured ? "border-emerald-500/40 text-emerald-600" : "border-slate-400/40 text-slate-500"}`} data-testid="ghl-status-badge">
          <Webhook className="h-3.5 w-3.5" />
          {settings?.configured ? t("Aktiv", "Active") : t("Nicht konfiguriert \u2013 Dry-Run-Modus", "Not configured \u2013 dry-run mode")}
        </Badge>
      </div>

      <Tabs defaultValue="settings">
        <TabsList className="rounded-[10px]">
          <TabsTrigger value="settings" className="rounded-[8px]" data-testid="crm-tab-settings">{t("Einstellungen", "Settings")}</TabsTrigger>
          <TabsTrigger value="events" className="rounded-[8px]" data-testid="crm-tab-events">Outbox ({events.length})</TabsTrigger>
          <TabsTrigger value="webhooks" className="rounded-[8px]" data-testid="crm-tab-webhooks">Webhooks ({webhooks.length})</TabsTrigger>
          <TabsTrigger value="mapping" className="rounded-[8px]" data-testid="crm-tab-mapping">{t("Mapping", "Mapping")}</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="mt-5">
          <Card className="rounded-[16px] max-w-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-[#BFA76A]" /> {t("API-Konfiguration", "API Configuration")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isSuperAdmin && (
                <p className="text-xs text-muted-foreground rounded-[10px] bg-muted p-3">
                  {t("Nur Super Admins k\u00f6nnen diese Einstellungen \u00e4ndern.", "Only super admins can change these settings.")}
                </p>
              )}
              <div className="space-y-1.5">
                <Label>API Key {settings?.api_key_set && <span className="text-xs text-muted-foreground">({t("gesetzt", "set")}: {settings.api_key_masked})</span>}</Label>
                <Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder={settings?.api_key_set ? t("Neuen Key eingeben zum \u00c4ndern", "Enter new key to change") : "ghl_..."} disabled={!isSuperAdmin} className="rounded-[10px]" data-testid="ghl-api-key-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Location ID</Label>
                <Input value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} disabled={!isSuperAdmin} className="rounded-[10px]" data-testid="ghl-location-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Webhook Secret {settings?.webhook_secret_set && <span className="text-xs text-muted-foreground">({t("gesetzt", "set")})</span>}</Label>
                <Input type="password" value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })} disabled={!isSuperAdmin} className="rounded-[10px]" data-testid="ghl-secret-input" />
                <p className="text-xs text-muted-foreground">{t("Eingehende Webhooks:", "Inbound webhooks:")} <code className="font-mono-num text-[11px]">POST /api/webhooks/ghl</code> (Header: x-webhook-secret)</p>
              </div>
              <label className="flex items-center gap-3 text-sm">
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} disabled={!isSuperAdmin} data-testid="ghl-enabled-switch" />
                {t("Synchronisierung aktivieren", "Enable synchronization")}
              </label>
              {isSuperAdmin && (
                <Button onClick={saveSettings} className="rounded-[10px] bg-[#0B1220]" data-testid="ghl-save-button">{t("Speichern", "Save")}</Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="mt-5">
          <Card className="rounded-[16px] overflow-hidden">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold">{t("Outbox-Events", "Outbox events")}</CardTitle>
              <Button variant="outline" size="sm" className="rounded-[10px]" onClick={retry} data-testid="crm-retry-button">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> {t("Erneut senden", "Retry")}
              </Button>
            </CardHeader>
            <div className="overflow-x-auto">
              <Table data-testid="crm-events-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Pipeline</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">{t("Versuche", "Attempts")}</TableHead>
                    <TableHead className="hidden sm:table-cell">{t("Erstellt", "Created")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((ev) => (
                    <TableRow key={ev.id} className="cursor-pointer" onClick={() => setSelectedEvent(ev)} data-testid={`crm-event-row-${ev.id}`}>
                      <TableCell>
                        <p className="text-sm font-medium">{ev.event_type}</p>
                        <p className="font-mono-num text-[10px] text-muted-foreground">{ev.id.slice(0, 8)}</p>
                      </TableCell>
                      <TableCell className="text-xs">{ev.pipeline}</TableCell>
                      <TableCell><Badge variant="outline" className={`rounded-full text-[10px] ${EVENT_STATUS_CLS[ev.status]}`}>{ev.status}</Badge></TableCell>
                      <TableCell className="hidden md:table-cell font-mono-num text-xs">{ev.attempts}</TableCell>
                      <TableCell className="hidden sm:table-cell text-xs text-muted-foreground font-mono-num">{new Date(ev.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  {events.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-10">{t("Keine Events vorhanden.", "No events available.")}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="webhooks" className="mt-5">
          <Card className="rounded-[16px]" data-testid="webhook-logs">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">{t("Eingehende Webhooks", "Inbound webhooks")}</CardTitle>
            </CardHeader>
            <CardContent>
              {webhooks.length === 0 ? (
                <div className="py-10 text-center">
                  <Send className="h-8 w-8 text-muted-foreground/40 mx-auto" />
                  <p className="mt-3 text-sm text-muted-foreground">{t("Noch keine Webhooks empfangen.", "No webhooks received yet.")}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Endpoint: <code className="font-mono-num">POST /api/webhooks/ghl</code></p>
                </div>
              ) : (
                <div className="space-y-2">
                  {webhooks.map((w) => (
                    <button key={w.id} onClick={() => setSelectedEvent(w)} className="w-full rounded-[12px] border border-border p-4 text-left hover:bg-muted/50 transition-colors flex items-center justify-between" data-testid={`webhook-row-${w.id}`}>
                      <div>
                        <p className="text-sm font-medium">{w.event_type}</p>
                        <p className="text-xs text-muted-foreground font-mono-num">{new Date(w.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex gap-2">
                        {!w.secret_valid && <Badge variant="outline" className="rounded-full text-[10px] border-red-500/40 text-red-600">{t("Ung\u00fcltiges Secret", "Invalid secret")}</Badge>}
                        <Badge variant="outline" className={`rounded-full text-[10px] ${w.processed ? EVENT_STATUS_CLS.sent : EVENT_STATUS_CLS.dry_run}`}>
                          {w.processed ? t("Verarbeitet", "Processed") : t("Empfangen", "Received")}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mapping" className="mt-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card className="rounded-[16px]">
              <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">{t("Formular \u2192 Pipeline", "Form \u2192 Pipeline")}</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(settings?.pipelines || {}).map(([form_type, pipeline]) => (
                  <div key={form_type} className="flex items-center justify-between rounded-[10px] bg-muted/50 px-4 py-2.5">
                    <code className="font-mono-num text-xs">{form_type}</code>
                    <Badge variant="secondary" className="rounded-full text-[10px]">{pipeline}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="rounded-[16px]">
              <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">{t("Automatische Tags", "Automatic tags")}</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(settings?.tags || {}).map(([form_type, tags]) => (
                  <div key={form_type} className="rounded-[10px] bg-muted/50 px-4 py-2.5">
                    <code className="font-mono-num text-xs">{form_type}</code>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {tags.map((tag) => <Badge key={tag} variant="outline" className="rounded-full text-[10px] font-mono-num">{tag}</Badge>)}
                      <Badge variant="outline" className="rounded-full text-[10px] font-mono-num">source_website</Badge>
                      <Badge variant="outline" className="rounded-full text-[10px] font-mono-num">status_new</Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedEvent} onOpenChange={(o) => !o && setSelectedEvent(null)}>
        <DialogContent className="sm:max-w-lg">
          {selectedEvent && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono-num text-sm">{selectedEvent.id}</DialogTitle>
              </DialogHeader>
              <ScrollArea className="max-h-[50vh]">
                <pre className="rounded-[12px] bg-[#0B1220] text-white/85 p-4 text-xs overflow-x-auto" data-testid="event-payload-json">
                  {JSON.stringify(selectedEvent.payload, null, 2)}
                </pre>
              </ScrollArea>
              {selectedEvent.last_error && (
                <p className="text-xs text-red-600">{selectedEvent.last_error}</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
